import {
  DEFAULT_SERVINGS,
  type DisplayUnit,
  type Ingredient,
  type Recipe,
} from '@/service/recipe/types';

// Firestore documents are not parsed, so older recipes have no servings
export function servingsOf(recipe: Pick<Recipe, 'servings'>): number {
  return recipe.servings || DEFAULT_SERVINGS;
}

export function scaleAmount(amount: number, from: number, to: number): number {
  return (amount * to) / from;
}

export function scaleIngredients(
  ingredients: Ingredient[],
  from: number,
  to: number,
): Ingredient[] {
  return ingredients.map((ingredient) => ({
    ...ingredient,
    amount: scaleAmount(ingredient.amount, from, to),
  }));
}

// Measured in fractions in the kitchen: spoons, cups and things you count
const fractionUnits: DisplayUnit[] = [
  'Teaspoon(s)',
  'Tablespoon(s)',
  'Cup(s)',
  'Piece(s)',
  'Pinch',
  'Clove(s)',
  'Can(s)',
  'Bunch',
  'Ounce(s)',
  'Pound(s)',
  '',
];
const largeUnits: DisplayUnit[] = ['Kilogram', 'Liter', 'Deciliter'];

const fractions: [number, string][] = [
  [1 / 8, '⅛'],
  [1 / 4, '¼'],
  [1 / 3, '⅓'],
  [1 / 2, '½'],
  [2 / 3, '⅔'],
  [3 / 4, '¾'],
];

function formatFraction(amount: number): string {
  let whole = Math.floor(amount);
  const rest = amount - whole;
  // The nearest of 0, the fractions and 1
  const candidates: [number, string][] = [[0, ''], ...fractions, [1, '']];
  const [value, glyph] = candidates.reduce((best, candidate) =>
    Math.abs(candidate[0] - rest) < Math.abs(best[0] - rest) ? candidate : best,
  );
  if (value === 1) {
    whole += 1;
  }
  if (whole === 0) {
    // Never round an ingredient away
    return glyph || '⅛';
  }
  return glyph ? `${whole} ${glyph}` : `${whole}`;
}

function formatDecimal(amount: number, decimals: number): string {
  // Tiny amounts keep their first significant digit instead of showing 0
  return String(
    Number(amount.toFixed(decimals)) || Number(amount.toPrecision(1)),
  );
}

// Rounds an amount the way it is measured: fractions for spoons, cups and
// pieces, whole grams and millilitres, a few decimals for kilos and litres
export function formatAmount(amount: number, unit: DisplayUnit): string {
  // Also covers a missing amount (NaN after scaling)
  if (!(amount > 0)) {
    return '';
  }
  if (fractionUnits.includes(unit)) {
    return amount >= 10 ? String(Math.round(amount)) : formatFraction(amount);
  }
  if (largeUnits.includes(unit)) {
    return formatDecimal(amount, 2);
  }
  // Milligram, Gram, Millilitre: whole numbers, except for small amounts
  return amount >= 10 ? String(Math.round(amount)) : formatDecimal(amount, 1);
}
